import { MessageBus } from '@podium/browser';
import type { SearchKey } from '@schibsted-nmp/search-frontend-clients/quest';
import React from 'react';
import { useAppConfig } from '../AppConfig';
import { usePolePositionData } from '../data/PolePositionData';
import type { SearchParams } from '../data/QueryKeys';
import { useSearchFilters } from '../data/SearchFilters';
import { useSearchLayout, type Layout } from '../data/SearchLayout';
import { trackAdClick, trackAdInscreen } from './tracking';
import { JobPolePositionAd } from './verticals/JobPolePositionAd';
import { MobilityPolePositionAd } from './verticals/MobilityPolePositionAd';
import { RealestatePolePositionAd } from './verticals/RealestatePolePositionAd';
import { RecommercePolePositionAd } from './verticals/RecommercePolePositionAd';

const messageBus = new MessageBus();

type PolePositionAdProps = {
    initialLayout: Layout;
    initialSearchKey: SearchKey;
    initialSearchParams: SearchParams;
};

export function PolePositionAd(props: PolePositionAdProps) {
    const { initialLayout, initialSearchKey, initialSearchParams } = props;

    const { searchKey, searchParams } = useSearchFilters(
        initialSearchKey,
        initialSearchParams,
    );

    const { renderTime } = useRenderTime();
    const { layout } = useSearchLayout(initialLayout);

    const { polePositionData } = usePolePositionData(searchKey, searchParams);

    React.useEffect(
        function publishStatusMessage() {
            const status = polePositionData?.result ? 'RESULT' : 'EMPTY';
            messageBus.publish('pole-position', 'status', status);
        },
        [polePositionData],
    );

    React.useEffect(
        function trackAdInscreenOnUpdatedSearch() {
            if (polePositionData?.result) {
                const { searchEntry, pulseTracking } = polePositionData.result;
                trackAdInscreen(searchEntry, pulseTracking);
            }
        },
        [polePositionData],
    );

    const handleAdClick = React.useCallback(() => {
        if (polePositionData?.result) {
            const { searchEntry, pulseTracking } = polePositionData.result;
            trackAdClick(searchEntry, pulseTracking);
        }
    }, [polePositionData]);

    if (!polePositionData?.result) {
        return null;
    }

    const { labels, searchEntry } = polePositionData.result;
    const ad = searchEntry;

    const adProps = {
        layout,
        onClick: handleAdClick,
        promotedLabel: labels[0],
        renderTime,
        searchKey,
    };

    if (ad.type === 'bap') {
        return <RecommercePolePositionAd ad={ad} {...adProps} />;
    } else if (ad.type === 'boat' || ad.type === 'motor') {
        return <MobilityPolePositionAd ad={ad} {...adProps} />;
    } else if (ad.type === 'job') {
        return <JobPolePositionAd ad={ad} {...adProps} />;
    } else if (ad.type === 'realestate' || ad.type === 'realestate_project') {
        return <RealestatePolePositionAd ad={ad} {...adProps} />;
    } else {
        return null;
    }
}

function useRenderTime() {
    const { currentTime } = useAppConfig();
    const renderTime = React.useRef<number | undefined>(currentTime);

    React.useEffect(
        function clearRenderTimeAfterClientSideHydration() {
            // The initial render timestamp is only need during client-side
            // hydration, to avoid inconsistent relative datetime formatting
            // caused by small time delays between server-side and client-side
            // rendering. For subsequent client-side rendering this is not a
            // problem, and we fallback to the browser's local time (in
            // combination with the server-defined locale).
            renderTime.current = undefined;
        },
        [renderTime],
    );

    return { renderTime: renderTime.current };
}
